import React, { useLayoutEffect, useRef, useState } from "react";
import { useFieldId, useFieldLabelId } from "../FieldWrapper/FieldWrapper";

interface Props {
  label: string;
  value?: React.ReactNode;
  icon?: React.ReactNode;
  /** Rendered at the end of the label row — e.g. a "Route" button. */
  action?: React.ReactNode;
  /**
   * Clamps long text to N lines with a "Mehr anzeigen" / "Weniger" toggle. Only meaningful for a
   * string `value` — replaces the fixed `!min-h-[100px]` textarea that used to both clip long text
   * and waste space on short text. The toggle only renders once the text actually overflows the
   * clamp — a short value never shows a "Mehr anzeigen" that has nothing more to reveal.
   */
  clampLines?: number;
  emptyText?: string;
  className?: string;
}

/**
 * The read-only replacement for `FieldWrapper` + a disabled `Input`. A disabled pill input is a
 * *write* affordance — nothing on this screen is writable, so the value renders as plain text: a
 * quiet label above a dark, legible value, `—` when empty.
 *
 * Carries the same white bordered card chrome as every other block on the page by default — a
 * bare, unboxed paragraph sitting directly below several bordered cards read as unstyled, not
 * "quieter". `className` is appended, not swapped in, so a caller can still add spacing utilities
 * without having to restate the card itself.
 *
 * Still consumes `useFieldId`/`useFieldLabelId` so a caller that nests this inside `FieldWrapper`
 * (none currently do, but the contract costs nothing to keep) stays correctly associated.
 */
export const ReadonlyField: React.FC<Props> = ({
  label,
  value,
  icon,
  action,
  clampLines,
  emptyText = "—",
  className,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const fieldId = useFieldId();
  const labelId = useFieldLabelId();

  const isEmpty = value === null || value === undefined || value === "";
  const isClampable = typeof value === "string" && !!clampLines;
  const canClamp = isClampable && !expanded;

  // Measured after every render of the clamped paragraph — a short value never overflows its own
  // clamp box, so the toggle only appears once there is genuinely more to reveal.
  useLayoutEffect(() => {
    if (!isClampable || expanded) return;
    const el = textRef.current;
    if (!el) return;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [isClampable, expanded, value]);

  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${className ?? ""}`}>
      <div className="flex items-center justify-between gap-2">
        <p id={labelId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {label}
        </p>
        {action}
      </div>
      <div id={fieldId} className="mt-1 flex items-start gap-2 text-base text-slate-900">
        {icon}
        <div className="min-w-0 flex-1">
          {isEmpty ? (
            <span className="text-slate-400">{emptyText}</span>
          ) : canClamp ? (
            // Tailwind's `line-clamp-N` needs a static class name at build time, and `clampLines`
            // is a runtime prop — the inline style is the one that actually clamps.
            <p
              ref={textRef}
              className="whitespace-pre-wrap break-words"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: clampLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {value}
            </p>
          ) : (
            <p className="whitespace-pre-wrap break-words">{value}</p>
          )}
          {isClampable && (overflowing || expanded) && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 text-sm font-medium text-primary"
            >
              {expanded ? "Weniger" : "Mehr anzeigen"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
